/**
 * Izosuprafețe prin volumele atmosferice.
 *
 * Extrage suprafața pe care câmpul are o valoare dată — conturul închis al
 * unui panaș de poluant, suprafața norilor la 90% umiditate, izoterma de 0 °C.
 * Spre deosebire de felii (plane) și de norul de puncte (densitate difuză),
 * asta e un obiect cu formă proprie, pe care îl poți da ocol.
 *
 * Trianghiularea se face prin **marching tetrahedra**, nu marching cubes:
 * fiecare celulă se împarte în 6 tetraedre în jurul diagonalei principale, iar
 * fiecare tetraedru are doar 7 cazuri distincte. Marching cubes ar fi cerut o
 * tabelă de 256×16 valori transcrisă manual — mai multe triunghiuri economisite,
 * dar mult mai multe ocazii de greșeală tăcută.
 *
 * Respectă contractul de primitivă Cesium, ca și celelalte straturi 3D.
 */
import {
  BlendingState, BoundingSphere, Buffer, BufferUsage, Cartesian3,
  ComponentDatatype, CullFace, DrawCommand, Matrix3, Matrix4, Pass,
  PrimitiveType, RenderState, ShaderProgram, Transforms, VertexArray,
} from 'cesium';

const M_PER_DEG_LAT = 110540;
const M_PER_DEG_LON_EQ = 111320;

// Cele 8 colțuri ale celulei, ca deplasări (nivel, lat, lon).
const CORNERS = [
  [0, 0, 0], [0, 0, 1], [0, 1, 1], [0, 1, 0],
  [1, 0, 0], [1, 0, 1], [1, 1, 1], [1, 1, 0],
];

// Descompunerea cubului în 6 tetraedre care împart diagonala 0–6.
const TETRA = [
  [0, 5, 1, 6], [0, 1, 2, 6], [0, 2, 3, 6],
  [0, 3, 7, 6], [0, 7, 4, 6], [0, 4, 5, 6],
];

const VERTEX_SHADER = `
in vec3 position;
in vec3 normal;
out vec3 v_normal;
void main() {
  v_normal = normal;
  gl_Position = czm_modelViewProjection * vec4(position, 1.0);
  czm_vertexLogDepth();
}
`;

// Iluminare pe două fețe: suprafața e închisă, deci o vedem și pe dinăuntru,
// iar o față neluminată ar apărea ca gaură neagră.
const FRAGMENT_SHADER = `
in vec3 v_normal;
uniform vec4 u_color;
uniform vec3 u_light;
void main() {
  vec3 n = normalize(v_normal);
  vec3 l = normalize(u_light);
  float lambert = max(max(dot(n, l), dot(-n, l)), 0.0);
  float shade = 0.38 + 0.62 * lambert;
  out_FragColor = vec4(u_color.rgb * shade, u_color.a);
  czm_writeLogDepth();
}
`;

/** Tampon care crește singur, ca să nu presupunem numărul de triunghiuri. */
class GrowableF32 {
  constructor(initial = 1 << 16) {
    this.buf = new Float32Array(initial);
    this.n = 0;
  }
  push3(a, b, c) {
    if (this.n + 3 > this.buf.length) {
      const bigger = new Float32Array(this.buf.length * 2);
      bigger.set(this.buf);
      this.buf = bigger;
    }
    this.buf[this.n++] = a;
    this.buf[this.n++] = b;
    this.buf[this.n++] = c;
  }
  view() {
    return this.buf.subarray(0, this.n);
  }
  reset() {
    this.n = 0;
  }
}

export class IsoSurface {
  /** @param {import('cesium').Viewer} viewer */
  constructor(viewer) {
    this.viewer = viewer;
    this.enabled = false;

    this.exaggeration = 2;
    this.opacity = 0.65;
    this.color = [255, 255, 255];
    this.threshold = 0;

    this._positions = new GrowableF32();
    this._normals = new GrowableF32();
    this._count = 0;

    this._center = Cartesian3.ZERO;
    this._radius = 1;
    this._enu = new Matrix3();
    // Direcția luminii, tot în coordonate geocentrice — vezi _rotateToEcef.
    this._light = { x: 0, y: 0, z: 1 };

    this._inScene = false;
    this._destroyed = false;
    this._dirty = false;
    this._capacity = 0;

    this._va = null;
    this._sp = null;
    this._rs = null;
    this._command = null;
    this._posBuffer = null;
    this._nrmBuffer = null;
  }

  /**
   * Reconstruiește izosuprafața.
   *
   * @param {{data: ArrayLike<number>, shape: number[]}} cube
   * @param {{lats: number[], lons: number[], altitudes: number[]}} grid
   * @param {number} iso  valoarea pragului
   * @returns {number} numărul de triunghiuri
   */
  rebuild(cube, { lats, lons, altitudes }, iso) {
    const [nlev, nlat, nlon] = cube.shape;
    const data = cube.data;
    this.threshold = iso;

    const midLon = (lons[0] + lons[lons.length - 1]) / 2;
    const midLat = (lats[0] + lats[lats.length - 1]) / 2;
    const topM = altitudes[altitudes.length - 1] * this.exaggeration;

    this._center = Cartesian3.fromDegrees(midLon, midLat, topM / 2);
    this._radius = Cartesian3.distance(
      this._center, Cartesian3.fromDegrees(lons[0], lats[0], topM)) * 1.25;

    // Normalele se calculează în cadrul local est–nord–sus. Pe 10° de
    // longitudine direcția „sus" se rotește cu ~5°, imperceptibil la umbrire,
    // deci folosim un singur cadru, al centrului, în loc de unul per vârf.
    Matrix4.getMatrix3(
      Transforms.eastNorthUpToFixedFrame(this._center), this._enu);

    // Lumina e definită natural în cadrul local (dinspre sud-vest, de sus),
    // dar normalele ajung în shader în coordonate geocentrice; dacă n-am roti-o
    // și pe ea, umbrirea ar veni dintr-o direcție arbitrară, dependentă de
    // poziția pe glob.
    this._light = this._rotateToEcef(-0.5, -0.4, 0.77);

    const gradients = this._computeGradients(data, nlev, nlat, nlon, lats, lons, altitudes);

    this._positions.reset();
    this._normals.reset();

    // Câmpurile de lucru pentru un tetraedru
    const px = new Float64Array(4), py = new Float64Array(4), pz = new Float64Array(4);
    const nx = new Float64Array(4), ny = new Float64Array(4), nz = new Float64Array(4);
    const fv = new Float64Array(4);

    const cLon = new Float64Array(8), cLat = new Float64Array(8), cAlt = new Float64Array(8);
    const cVal = new Float64Array(8);
    const cNx = new Float64Array(8), cNy = new Float64Array(8), cNz = new Float64Array(8);

    for (let l = 0; l < nlev - 1; l++) {
      for (let y = 0; y < nlat - 1; y++) {
        for (let x = 0; x < nlon - 1; x++) {
          // Colțurile celulei
          let below = 0, above = 0;
          for (let c = 0; c < 8; c++) {
            const [dl, dy, dx] = CORNERS[c];
            const li = l + dl, yi = y + dy, xi = x + dx;
            const idx = li * nlat * nlon + yi * nlon + xi;
            const v = data[idx];
            cVal[c] = v;
            cLon[c] = lons[xi];
            cLat[c] = lats[yi];
            cAlt[c] = altitudes[li];
            cNx[c] = gradients[idx * 3];
            cNy[c] = gradients[idx * 3 + 1];
            cNz[c] = gradients[idx * 3 + 2];
            if (v < iso) below++; else above++;
          }
          // Celula e complet de o parte a pragului — nu o traversează.
          if (below === 0 || above === 0) continue;

          for (const tet of TETRA) {
            for (let i = 0; i < 4; i++) {
              const c = tet[i];
              fv[i] = cVal[c];
              px[i] = cLon[c]; py[i] = cLat[c]; pz[i] = cAlt[c];
              nx[i] = cNx[c]; ny[i] = cNy[c]; nz[i] = cNz[c];
            }
            this._marchTetra(fv, px, py, pz, nx, ny, nz, iso);
          }
        }
      }
    }

    this._count = this._positions.n / 3;
    this._dirty = true;
    this._capacity = 0;   // bufferele GL se recreează pentru noua dimensiune
    this._releaseGl();

    this.viewer.scene.requestRender();
    return this._count / 3;
  }

  /** Rotește un vector din cadrul local est–nord–sus în cel geocentric. */
  _rotateToEcef(e, n, u) {
    const m = this._enu;
    return {
      x: m[0] * e + m[3] * n + m[6] * u,
      y: m[1] * e + m[4] * n + m[7] * u,
      z: m[2] * e + m[5] * n + m[8] * u,
    };
  }

  /**
   * Gradientul câmpului, în unități fizice.
   *
   * Normala izosuprafeței e gradientul, orientat spre valori descrescătoare.
   * Componenta verticală se împarte la exagerare fiindcă geometria desenată e
   * întinsă pe verticală, iar umbrirea trebuie să corespundă formei văzute,
   * nu celei reale.
   */
  _computeGradients(data, nlev, nlat, nlon, lats, lons, altitudes) {
    const g = new Float32Array(nlev * nlat * nlon * 3);
    const dLat = nlat > 1 ? Math.abs(lats[1] - lats[0]) * M_PER_DEG_LAT : 1;

    for (let l = 0; l < nlev; l++) {
      const lm = Math.max(0, l - 1), lp = Math.min(nlev - 1, l + 1);
      const dz = Math.max(1, (altitudes[lp] - altitudes[lm])) * this.exaggeration;

      for (let y = 0; y < nlat; y++) {
        const ym = Math.max(0, y - 1), yp = Math.min(nlat - 1, y + 1);
        const dy = (yp - ym) * dLat;
        const dLon = nlon > 1
          ? Math.abs(lons[1] - lons[0]) * M_PER_DEG_LON_EQ * Math.cos(lats[y] * Math.PI / 180)
          : 1;

        for (let x = 0; x < nlon; x++) {
          const xm = Math.max(0, x - 1), xp = Math.min(nlon - 1, x + 1);
          const dx = Math.max(1, (xp - xm) * dLon);
          const base = l * nlat * nlon + y * nlon + x;

          const gx = (data[l * nlat * nlon + y * nlon + xp]
                    - data[l * nlat * nlon + y * nlon + xm]) / dx;
          const gy = (data[l * nlat * nlon + yp * nlon + x]
                    - data[l * nlat * nlon + ym * nlon + x]) / Math.max(1, dy);
          const gz = (data[lp * nlat * nlon + y * nlon + x]
                    - data[lm * nlat * nlon + y * nlon + x]) / dz;

          // Spre exteriorul regiunii cu valori mari
          g[base * 3] = -gx;
          g[base * 3 + 1] = -gy;
          g[base * 3 + 2] = -gz;
        }
      }
    }
    return g;
  }

  /**
   * Cele 7 cazuri distincte ale unui tetraedru.
   *
   * Orientarea triunghiurilor nu contează: desenăm fără eliminarea fețelor
   * din spate și cu iluminare pe două fețe.
   */
  _marchTetra(f, px, py, pz, nx, ny, nz, iso) {
    let idx = 0;
    if (f[0] < iso) idx |= 1;
    if (f[1] < iso) idx |= 2;
    if (f[2] < iso) idx |= 4;
    if (f[3] < iso) idx |= 8;

    const E = (a, b) => this._edge(a, b, f, px, py, pz, nx, ny, nz, iso);

    switch (idx) {
      case 0x00: case 0x0F:
        return;
      case 0x01: case 0x0E:
        E(0, 1); E(0, 2); E(0, 3); break;
      case 0x02: case 0x0D:
        E(1, 0); E(1, 3); E(1, 2); break;
      case 0x03: case 0x0C:
        E(0, 3); E(0, 2); E(1, 3);
        E(1, 3); E(1, 2); E(0, 2); break;
      case 0x04: case 0x0B:
        E(2, 0); E(2, 1); E(2, 3); break;
      case 0x05: case 0x0A:
        E(0, 1); E(1, 2); E(0, 3);
        E(1, 2); E(2, 3); E(0, 3); break;
      case 0x06: case 0x09:
        E(0, 1); E(0, 2); E(1, 3);
        E(0, 2); E(2, 3); E(1, 3); break;
      case 0x07: case 0x08:
        E(3, 0); E(3, 2); E(3, 1); break;
    }
  }

  /** Punctul de pe muchia a–b unde câmpul atinge pragul. */
  _edge(a, b, f, px, py, pz, nx, ny, nz, iso) {
    const fa = f[a], fb = f[b];
    let t = fb === fa ? 0.5 : (iso - fa) / (fb - fa);
    if (t < 0) t = 0; else if (t > 1) t = 1;

    const lon = px[a] + (px[b] - px[a]) * t;
    const lat = py[a] + (py[b] - py[a]) * t;
    const alt = pz[a] + (pz[b] - pz[a]) * t;

    const p = Cartesian3.fromDegrees(lon, lat, alt * this.exaggeration);
    this._positions.push3(
      p.x - this._center.x, p.y - this._center.y, p.z - this._center.z);

    // Normala, interpolată apoi rotită din cadrul local în cel geocentric
    let e = nx[a] + (nx[b] - nx[a]) * t;
    let n = ny[a] + (ny[b] - ny[a]) * t;
    let u = nz[a] + (nz[b] - nz[a]) * t;
    const len = Math.hypot(e, n, u) || 1;
    e /= len; n /= len; u /= len;

    const v = this._rotateToEcef(e, n, u);
    this._normals.push3(v.x, v.y, v.z);
  }

  get triangleCount() {
    return this._count / 3;
  }

  show() {
    this.enabled = true;
    if (!this._inScene) {
      this.viewer.scene.primitives.add(this);
      this._inScene = true;
    }
    this.viewer.scene.requestRender();
  }

  hide() {
    this.enabled = false;
    this.viewer.scene.requestRender();
  }

  // ── Contract de primitivă Cesium ────────────────────────────────
  update(frameState) {
    if (!this.enabled || this._destroyed || this._count === 0) return;

    const context = frameState.context;
    if (!this._command) this._createResources(context);
    else if (this._dirty) {
      this._posBuffer.copyFromArrayView(this._positions.view(), 0);
      this._nrmBuffer.copyFromArrayView(this._normals.view(), 0);
      this._command.count = this._count;
      this._dirty = false;
    }

    frameState.commandList.push(this._command);
  }

  _createResources(context) {
    this._posBuffer = Buffer.createVertexBuffer({
      context, typedArray: this._positions.view(), usage: BufferUsage.STATIC_DRAW,
    });
    this._nrmBuffer = Buffer.createVertexBuffer({
      context, typedArray: this._normals.view(), usage: BufferUsage.STATIC_DRAW,
    });
    this._dirty = false;

    this._va = new VertexArray({
      context,
      attributes: [
        {
          index: 0, vertexBuffer: this._posBuffer,
          componentsPerAttribute: 3, componentDatatype: ComponentDatatype.FLOAT,
        },
        {
          index: 1, vertexBuffer: this._nrmBuffer,
          componentsPerAttribute: 3, componentDatatype: ComponentDatatype.FLOAT,
        },
      ],
    });

    this._sp = ShaderProgram.fromCache({
      context,
      vertexShaderSource: VERTEX_SHADER,
      fragmentShaderSource: FRAGMENT_SHADER,
      attributeLocations: { position: 0, normal: 1 },
    });

    this._rs = RenderState.fromCache({
      depthTest: { enabled: true },
      depthMask: false,
      // Suprafața e închisă și translucidă: ambele fețe trebuie desenate.
      cull: { enabled: false, face: CullFace.BACK },
      blending: BlendingState.ALPHA_BLEND,
    });

    this._command = new DrawCommand({
      vertexArray: this._va,
      shaderProgram: this._sp,
      renderState: this._rs,
      pass: Pass.TRANSLUCENT,
      primitiveType: PrimitiveType.TRIANGLES,
      modelMatrix: Matrix4.fromTranslation(this._center),
      boundingVolume: new BoundingSphere(this._center, this._radius),
      count: this._count,
      owner: this,
      uniformMap: {
        u_color: () => ({
          x: this.color[0] / 255, y: this.color[1] / 255,
          z: this.color[2] / 255, w: this.opacity,
        }),
        u_light: () => this._light,
      },
    });
  }

  _releaseGl() {
    if (this._va) this._va.destroy();
    if (this._sp) this._sp.destroy();
    this._va = null;
    this._sp = null;
    this._command = null;
    this._posBuffer = null;
    this._nrmBuffer = null;
  }

  isDestroyed() {
    return this._destroyed;
  }

  destroy() {
    this._destroyed = true;
    this._releaseGl();
    return undefined;
  }
}
